import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { ReportItemDisplayInfoLookup, ReportTotalItem } from '@/constants/ReportDashboard/report-items'
import { useDataContext, useIsAnalysisLoading, usePortfolioReportTotalItem } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import useElementDimensions from '@/utils/useElementDimensions'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

export type DataPointCardProps = CardProps & {
  dataPoint: ReportTotalItem
}

export type DataPointCardDisplayInfo = {
  description: string
  displayFormat?: string
  maxDecimals?: number
  kind?: 'rating'
}

function valueFormatFromDisplayFormat(displayFormat?: string): 'decimal' | 'currency' | 'percent' {
  if (displayFormat === '%') return 'percent'
  if (displayFormat === 'Base CCY' || displayFormat === 'Local CCY') return 'currency'
  return 'decimal'
}

function numOverNDigits(num: number, digits: number) {
  return num / Math.pow(10, digits) >= 1
}

const cardLayout = {
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'visible',
  padding: '12px',
  boxSizing: 'border-box',
}

const valueContainerStyle = {
  textAlign: 'center' as const,
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
}
const DEFAULT_DECIMAL_PLACES = 2
const STANDARD_DIGIT_LIMIT = 3

//Responsive sizing consts
const REFERENCE_CARD_WIDTH = 280
const MIN_SCALE = 0.33
const VALUE_FONT_SIZE = 24
const SUFFIX_FONT_SIZE = 20
const CHANGE_FONT_SIZE = 16

const DataPointCard: React.FC<DataPointCardProps> = (props) => {
  const theme = useTheme()
  const isLoading = useIsAnalysisLoading()
  const reportData = usePortfolioReportTotalItem(props.dataPoint)
  const baseCurrency = useDataContext().baseCurrency
  const displayInfo = ReportItemDisplayInfoLookup[props.dataPoint]
  const valueFormat = valueFormatFromDisplayFormat(displayInfo.displayFormat)
  // For 'Base CCY' cards, render the portfolio's base currency code (e.g. "USD") as the suffix
  // next to the value and format the number as a plain decimal — otherwise Intl would prepend "$" / "US$".
  const useBaseCurrencySuffix = displayInfo.displayFormat === 'Base CCY' && !!baseCurrency
  const effectiveValueFormat = useBaseCurrencySuffix ? 'decimal' : valueFormat

  const { dimensions, dimensionsRef } = useElementDimensions<HTMLDivElement>()
  const scale = dimensions
    ? Math.max(MIN_SCALE, Math.min(1, dimensions.width / REFERENCE_CARD_WIDTH))
    : 1

  const isRating = displayInfo.isRating
  const dataValue = reportData?.value ?? null
  const pctChange = reportData?.pctChange ?? null
  const valueString = reportData?.valueString ?? null
  const ratingChange = reportData?.ratingChange ?? null

  const numFormatterValue = new Intl.NumberFormat('en-US', {
    style: effectiveValueFormat,
    maximumFractionDigits: displayInfo.displayPrecision ?? DEFAULT_DECIMAL_PLACES,
    currency: 'USD',
    notation: dataValue !== null && numOverNDigits(dataValue, STANDARD_DIGIT_LIMIT) ? 'compact' : 'standard',
  })

  const suffixMap: Record<string, string> = { Years: 'Yrs', 'bp-yr': 'bp-yr', bps: 'bps' }
  const valueSuffix = useBaseCurrencySuffix
    ? baseCurrency
    : displayInfo.displayFormat ? (suffixMap[displayInfo.displayFormat] ?? null) : null
  const nullDisplay = isLoading ? '--' : 'N/A'
  const formattedValue = isRating
    ? (valueString ?? nullDisplay)
    : dataValue != null
      ? numFormatterValue.format(effectiveValueFormat !== 'percent' ? dataValue : dataValue / 100)
      : nullDisplay

  const precisionFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: effectiveValueFormat,
        maximumFractionDigits: 12,
        currency: 'USD',
        notation: 'standard',
      }),
    [effectiveValueFormat],
  )
  const tooltipTitle =
    !isRating && dataValue != null
      ? `${precisionFormatter.format(effectiveValueFormat !== 'percent' ? dataValue : dataValue / 100)}${valueSuffix ? ` ${valueSuffix}` : ''}`
      : ''

  return (
    <div
      ref={dimensionsRef}
      style={{ width: '100%', height: '100%' }}
    >
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack
        sx={{
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <Typography sx={{ fontSize: `14px`, fontWeight: 'bold', color: theme.textPrimary, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {CardTitleLookup[props.cardId]}
        </Typography>
      </Stack>
      <Stack sx={valueContainerStyle}>
        <Stack direction='row' alignItems='baseline' spacing={1}>
          <Tooltip
            title={tooltipTitle}
            arrow
            disableHoverListener={!tooltipTitle}
            slotProps={{
              tooltip: { sx: { fontSize: '14px', padding: '8px 12px', maxWidth: 320 } },
              arrow: { sx: { fontSize: '14px' } },
            }}
          >
            <Typography
              component='span'
              sx={{
                fontSize: `${VALUE_FONT_SIZE * scale}px`,
                fontWeight: '700',
                color: theme.textPrimary,
              }}
            >
              {formattedValue}
            </Typography>
          </Tooltip>
          {!isRating && valueSuffix && dataValue !== null && (
            <Typography
              sx={{
                fontSize: `${SUFFIX_FONT_SIZE * scale}px`,
                fontWeight: '600',
                color: theme.textSecondary,
              }}
            >
              {valueSuffix}
            </Typography>
          )}
          {!isRating && pctChange !== null && (
            <Typography
              sx={{
                fontSize: `${CHANGE_FONT_SIZE * scale}px`,
                fontWeight: 600,
                color: pctChange >= 0 ? '#2e7d32' : '#c62828',
              }}
            >
              {pctChange >= 0 ? '+' : ''}
              {pctChange.toFixed(2)}%
            </Typography>
          )}
          {isRating && ratingChange && (
            <Typography
              sx={{
                fontSize: `${CHANGE_FONT_SIZE * scale}px`,
                fontWeight: 600,
                color: ratingChange === 'Upgrade' ? '#2e7d32' : ratingChange === 'Downgrade' ? '#c62828' : theme.textSecondary,
              }}
            >
              {ratingChange}
            </Typography>
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: `14px`,
            color: theme.textSecondary,
          }}
        >
          {displayInfo.description}
        </Typography>
      </Stack>
    </Stack>
    </div>
  )
}

export default DataPointCard
